import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { ArrowRight, Mail, Lock, Eye, EyeOff, LifeBuoy } from "lucide-react";
import RestoreAccountDialog from "@/components/auth/RestoreAccountDialog";

// VIS-004 · Public sign-in page (returning-user experience). Bilingual EN + ES.
// Approved production composition: warm desk scene with Levi wearing the
// JobClose AI cap. This page is intentionally NOT a marketing page —
// the visitor already chose JobClose AI and is coming back to work.
// Existing authentication — AuthContext.login, admin/contractor redirect,
// form validation, toast messaging, password visibility toggle, and route
// — is preserved without modification. Forgot-password remains intentionally
// unavailable; Contact Support uses the existing public support route.

const COPY_LOGIN = {
  en: {
    tagline: "Built by a contractor, for contractors.",
    kicker: "Good to see you again.",
    h1_a: "Welcome back.",
    h1_b: "Levi\u2019s ready when you are.",
    lead: "Pick up where you left off.",
    body:
      "Your customers, projects, estimates, documents, schedules and job details are right where you left them.",
    overline: "Sign in",
    panel_heading: "Welcome back.",
    panel_sub: "Sign in to continue.",
    label_email: "Email",
    ph_email: "you@company.com",
    label_password: "Password",
    show_pw: "Show password",
    hide_pw: "Hide password",
    submit_idle: "Sign in",
    submit_loading: "Signing in\u2026",
    or: "or",
    forgot: "Forgot password?",
    contact: "Contact Support",
    copyright: "All rights reserved.",
  },
  es: {
    tagline: "Creado por un contratista, para contratistas.",
    kicker: "Qu\u00e9 bueno verte de nuevo.",
    h1_a: "Bienvenido de nuevo.",
    h1_b: "Levi est\u00e1 listo cuando t\u00fa lo est\u00e9s.",
    lead: "Contin\u00faa donde lo dejaste.",
    body:
      "Tus clientes, proyectos, estimaciones, documentos, calendarios y detalles de trabajo est\u00e1n justo donde los dejaste.",
    overline: "Iniciar sesi\u00f3n",
    panel_heading: "Bienvenido de nuevo.",
    panel_sub: "Inicia sesi\u00f3n para continuar.",
    label_email: "Correo electr\u00f3nico",
    ph_email: "tu@empresa.com",
    label_password: "Contrase\u00f1a",
    show_pw: "Mostrar contrase\u00f1a",
    hide_pw: "Ocultar contrase\u00f1a",
    submit_idle: "Iniciar sesi\u00f3n",
    submit_loading: "Iniciando sesi\u00f3n\u2026",
    or: "o",
    forgot: "\u00bfOlvidaste tu contrase\u00f1a?",
    contact: "Contactar a soporte",
    copyright: "Todos los derechos reservados.",
  },
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { t, lang, setLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  // QA escape hatch (matches /signup): allow `/login?lang=es` or `?lang=en`
  // as a direct, clickable deep-link for Product Owner visual review
  // without opening DevTools. Runs once when the URL param differs from
  // the current locale; harmless when absent.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = (params.get("lang") || "").toLowerCase();
    if ((q === "es" || q === "en") && q !== lang) {
      setLang(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const c = COPY_LOGIN[lang] || COPY_LOGIN.en;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`${t("login.panel_heading")} — ${u.full_name.split(" ")[0]}`);
      navigate(u.role === "admin" ? "/app/admin" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login.error_default"));
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full pl-11 pr-3.5 py-3 rounded-lg border border-[var(--jc-border)] bg-white text-[var(--jc-text)] placeholder:text-[var(--jc-text-soft)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] focus:border-[var(--jc-blue)] transition";

  return (
    <div className="min-h-screen grid lg:grid-cols-[8fr_5fr] bg-white" data-jc-lang={lang}>
      {/* LEFT PANEL — approved production desk composition (Levi in the
          JobClose AI cap, laptop, blueprints, tape measure, today's-plan
          notepad, warm lamp light). Native welcome copy sits over the
          intentional open field on the left. Static artwork — no overlay,
          no animation, no additional artwork. */}
      <div
        className="hidden lg:flex relative flex-col overflow-hidden bg-white"
        style={{
          backgroundImage: "url('/brand/login-left-panel-levi-desk-v2.png')",
          backgroundSize: "cover",
          backgroundPosition: "30% center",
          backgroundRepeat: "no-repeat",
        }}
        data-testid="login-left-panel"
      >
        <div className="relative p-10 xl:p-14 flex flex-col h-full">
          {/* Brand + tagline — matches the visual system established on
              Signup so the returning-user page reads as part of the same
              modernized public shell. */}
          <a
            href="/"
            aria-label="JobClose AI"
            className="inline-flex items-center gap-3 select-none"
            data-testid="login-logo"
          >
            <img
              src="/brand/jobclose-ai-approved-logo-mark.png"
              alt=""
              aria-hidden="true"
              style={{ height: 56, width: "auto" }}
              className="shrink-0"
              draggable={false}
            />
            <span className="font-display text-3xl xl:text-4xl font-bold tracking-tight text-[var(--jc-text)] whitespace-nowrap leading-none">
              JobClose <span className="text-[var(--jc-blue)]">AI</span>
            </span>
          </a>
          <div className="mt-2 text-[11.5px] font-bold tracking-[0.18em] uppercase text-[var(--jc-text-soft)]">
            {c.tagline}
          </div>

          {/* Kicker + welcome headline + supporting continuity copy.
              Constrained width keeps every line inside the intentional
              open field of the composition. */}
          <div className="mt-10 xl:mt-14 max-w-[440px]">
            <div
              data-testid="login-hero-kicker"
              className="text-[13.5px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)]"
            >
              {c.kicker}
            </div>

            <h2
              data-testid="login-hero-headline"
              className="mt-4 font-display font-extrabold text-4xl xl:text-5xl leading-[1.05] tracking-tight text-[var(--jc-text)]"
            >
              {c.h1_a}<br />
              <span className="text-[var(--jc-blue)]">{c.h1_b}</span>
            </h2>

            <div className="mt-6 h-[3px] w-16 bg-[var(--jc-blue)] rounded-full" />

            <p className="mt-6 text-[20px] leading-[1.65] text-[var(--jc-text)] font-bold">
              {c.lead}
            </p>
            <p className="mt-3 text-[19px] leading-[1.65] text-[var(--jc-text)] font-medium max-w-[460px]">
              {c.body}
            </p>
          </div>

          <div
            className="mt-auto text-xs font-semibold text-[var(--jc-text)]"
            style={{ textShadow: "0 1px 2px rgba(255,255,255,0.55)" }}
            data-testid="login-copyright"
          >
            &copy; {new Date().getFullYear()} JobClose AI. {c.copyright}
          </div>
        </div>
      </div>

      {/* RIGHT PANEL — approved sign-in form (existing auth preserved). */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-14 bg-white">
        <div className="w-full max-w-md">
          {/* MOBILE-ONLY returning-user stack — renders the same approved
              welcome-back story before the form. Desktop keeps the left
              visual panel; on mobile we render it inline in this order:
              brand → kicker → headline → lead → body → Levi visual, then
              the existing overline/heading/form continues below. */}
          <div className="lg:hidden mb-10 space-y-6" data-testid="login-mobile-stack">
            {/* 1 · Compact brand header */}
            <div className="flex justify-center">
              <Logo
                variant="full"
                theme="dark"
                size="xl"
                to="/"
                data-testid="login-logo-mobile"
              />
            </div>

            {/* 2 · Kicker · 3 · Headline · 4 · Lead · 5 · Supporting copy */}
            <div>
              <div
                data-testid="login-hero-kicker-mobile"
                className="text-[13.5px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)]"
              >
                {c.kicker}
              </div>
              <h2
                data-testid="login-hero-headline-mobile"
                className="mt-3 font-display font-extrabold text-[34px] sm:text-[40px] leading-[1.05] tracking-tight text-[var(--jc-text)]"
              >
                {c.h1_a}<br />
                <span className="text-[var(--jc-blue)]">{c.h1_b}</span>
              </h2>
              <div className="mt-5 h-[3px] w-16 bg-[var(--jc-blue)] rounded-full" />
              <p className="mt-5 text-[19px] leading-[1.6] text-[var(--jc-text)] font-bold">
                {c.lead}
              </p>
              <p className="mt-3 text-[17px] leading-[1.6] text-[var(--jc-text)] font-medium">
                {c.body}
              </p>
            </div>

            {/* 6 · Levi visual — approved v2 desk composition, rendered
                inline at natural landscape aspect (full-bleed to mobile
                viewport edges for stronger visual presence). No crop, no
                text overlay, Levi fully visible. */}
            <div
              className="relative -mx-6 sm:-mx-10 overflow-hidden ring-1 ring-black/10 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.35)]"
              data-testid="login-mobile-photo"
            >
              <img
                src="/brand/login-left-panel-levi-desk-v2.png"
                alt="Levi, a black Goldendoodle wearing a JobClose AI cap and blue collar with a LEVI tag, sits at a home-office desk with a rolled blueprint, framed PLAN BUILD DELIVER poster, lamp, and open blueprints in front of him."
                className="block w-full h-auto select-none"
                draggable={false}
              />
            </div>
          </div>

          <div className="mb-2" data-testid="login-overline">
            <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)]">
              {c.overline}
            </span>
          </div>

          <h1
            data-testid="login-panel-heading"
            className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[var(--jc-text)]"
          >
            {c.panel_heading}
          </h1>
          <p className="mt-2 text-[var(--jc-text-soft)]" data-testid="login-panel-sub">
            {c.panel_sub}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label
                htmlFor="login-email"
                className="block text-sm font-semibold text-[var(--jc-text)] mb-1.5"
              >
                {c.label_email}
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)]" />
                <input
                  id="login-email"
                  type="email"
                  required
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputBase}
                  placeholder={c.ph_email}
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="block text-sm font-semibold text-[var(--jc-text)] mb-1.5"
              >
                {c.label_password}
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)]" />
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputBase} pr-11`}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-password-toggle"
                  aria-label={showPassword ? c.hide_pw : c.show_pw}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] transition"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg font-semibold text-white bg-[var(--jc-navy)] hover:bg-[#0f1a3d] transition disabled:opacity-60 shadow-sm"
            >
              {loading ? c.submit_loading : (<>{c.submit_idle} <ArrowRight size={16} /></>)}
            </button>
          </form>

          {/* Thin "or" divider */}
          <div className="mt-6 flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-[var(--jc-border)]" />
            <span className="text-xs text-[var(--jc-text-soft)]">{c.or}</span>
            <span className="h-px flex-1 bg-[var(--jc-border)]" />
          </div>

          {/* Secondary support row. Forgot-password remains intentionally
              deferred. Contact Support uses the existing public route. */}
          <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
            <span
              data-testid="login-forgot-password-placeholder"
              aria-disabled="true"
              title="Password reset is not currently available"
              className="inline-flex items-center justify-center gap-2 text-[var(--jc-text-soft)] font-medium opacity-70 cursor-not-allowed select-none"
            >
              <Lock size={16} aria-hidden="true" /> {c.forgot}
            </span>
            <Link
              to="/support"
              data-testid="login-contact-support-link"
              className="inline-flex items-center justify-center gap-2 text-[var(--jc-blue)] font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jc-blue)] focus-visible:ring-offset-2 rounded-sm"
            >
              <LifeBuoy size={16} aria-hidden="true" /> {c.contact}
            </Link>
          </div>

          {/* Account Deletion Safety Bundle A follow-up (2026-08-25) ·
              Restore My Account entry point. Uses the existing
              POST /api/auth/cancel-account-deletion-by-email endpoint;
              inherits the Bundle B 409 purge-in-progress semantics. */}
          <div className="mt-4 flex justify-center">
            <RestoreAccountDialog lang={lang} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
